import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
//third party
import {
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  map,
  throwError,
  timer,
} from 'rxjs';
import * as ExcelJS from 'exceljs';
// models
import {
  Account,
  Contact,
  Customer,
  EditCustomerResponsePackage,
  EditVendorResponsePackage,
  Expense,
  FormStatus,
  ProductServiceItem,
  SalesReceipt,
  salesTax,
  Vendor,
} from 'src/app/shared/models/accounting';
import {
  AutoPurchase,
  InventoryAlert,
  PurchaseHistory,
  saveProductServiceResponse,
} from 'src/app/shared/models/general';
import { Master, AccountingOptions } from 'src/app/shared/models/company';
import {
  ComponentNames,
  ContactTypes,
  InventoryTypes,
  ModalID,
  Statements,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import { SharedService } from 'src/app/shared/services/shared.service';
import { LocalDatabaseService } from 'src/app/shared/services/local-database.service';
import { NetworkSyncService } from 'src/app/shared/services/network-sync.service';
import {
  WebsocketService,
  CollectionChangeEvent,
} from 'src/app/shared/services/websocket.service';
import { filter } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { LiveVendorData } from 'src/app/shared/models/vendor';
import { ColumnState } from 'ag-grid-community';
import {
  transformVendorToPlainObject,
  sortByDisplayName,
} from '../shared/functions';
@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  create_vendor_credit$: Subject<boolean> = new Subject();
  expense_created$: Subject<Expense> = new Subject();
  expense_edited$: Subject<Expense> = new Subject();
  sales_receipt_created$: Subject<SalesReceipt> = new Subject();
  sales_receipt_edited$: Subject<SalesReceipt> = new Subject();
  sales_records_list_retrieved$: Subject<SalesReceipt[]> = new Subject();
  sales_receipt_list_updated$: Subject<SalesReceipt[]> = new Subject();
  product_service_retrieved$: Subject<ProductServiceItem[]> = new Subject();
  product_service_created$: Subject<saveProductServiceResponse> = new Subject();
  product_service_to_add$: Subject<saveProductServiceResponse> = new Subject();
  product_service_to_edit$: Subject<saveProductServiceResponse> = new Subject();
  products_services_list_updated$: Subject<ProductServiceItem[]> =
    new Subject();
  product_service_modal_canceled$: Subject<any> = new Subject();
  create_invoice$: Subject<any> = new Subject();
  customer_contact_to_add$: Subject<Customer> = new Subject();
  vendor_contact_to_add$: Subject<Vendor> = new Subject();
  vendor_contact_modal_closed$: Subject<boolean> = new Subject();
  vendor_added$: Subject<Vendor> = new Subject();
  vendor_list_retrieved$: Subject<Vendor[]> = new Subject();
  vendor_list_updated$: Subject<Vendor[]> = new Subject();
  customer_added$: Subject<Customer> = new Subject();
  customer_list_retrieved$: Subject<Customer[]> = new Subject();
  customer_created$: Subject<Customer> = new Subject();
  customer_list_updated$: Subject<Customer[]> = new Subject();
  customer_contact_to_edit$: Subject<EditCustomerResponsePackage> =
    new Subject();
  vendor_contact_to_edit$: Subject<EditVendorResponsePackage> = new Subject();
  file_verified$: Subject<{ file: File; contact_type: string }> = new Subject(); // moved to sharedService
  categories_selected_for_import$: Subject<string[]> = new Subject();
  sales_records_list_updated$: Subject<SalesReceipt[]> = new Subject();
  sales_receipt_deleted$: Subject<SalesReceipt[]> = new Subject();
  sales_receipt_updated$: Subject<SalesReceipt[]> = new Subject();
  expense_records_list_updated$: Subject<Expense[]> = new Subject();
  expense_records_list_retrieved$: Subject<Expense[]> = new Subject();

  // ag-grid stored filtering and column_config
  current_session_filtering: {
    component_name: string;
    filters?: {};
    column_config?: ColumnState[];
  }[] = [
    {
      component_name: ComponentNames.product_services,
      filters: ({} = {}),
      column_config: ([] = []),
    },
  ];

  cancelChoice$: Subject<{ options: string[]; optional_details: {} }> =
    new Subject();
  serial_category: string[] = [
    'RIFLE',
    'PISTOL',
    'SHOTGUN',
    'REVOLVER',
    'SILENCER',
    'RECEIVER',
    'FRAME',
  ];
  /** Built-in payment accounts every company always has — render read-only
   * at the top of the Payment Options column, can't be removed or reordered.
   * Keep this list small and universal (bank-specific names belong in the
   * per-company customs). To add a new default, edit this array. */
  payment_account_defaults: string[] = [
    'N/A',
    'Undeposited Funds',
    'Inventory Asset',
  ];
  /** Built-in payment methods every company always has — same rules as
   * payment_account_defaults. */
  payment_method_defaults: string[] = [
    'American Express',
    'Cash',
    'Check',
    'Debit Card',
    'Discover',
    'MasterCard',
    'Money Order',
    'PayPal',
    'Venmo',
    'Visa',
    'N/A',
  ];

  /** Runtime merged list: defaults + saved customs. Used by the Expense
   * form dropdowns. Populated by mergeCustomAccounts(). */
  payment_accounts: string[] = [...this.payment_account_defaults];
  payment_methods: string[] = [...this.payment_method_defaults];
  categories: string[] = [
    'Shipping',
    'Office Supplies',
    'Rent',
    'Utilities',
    'Meals & Entertainment',
    'Travel',
    'Advertising',
    'Insurance',
    'Legal & Professional Services',
    'Repairs & Maintenance',
    'Other',
  ];
  inventory_asset_accounts: string[] = [
    'Inventory Asset',
    'Furniture & Equipment',
  ];
  income_expense_accounts: Account[] = [
    // Income accounts
    { type: 'Income', name: 'Merchandise Sales' },
    { type: 'Income', name: 'FFL Transfer Fees' },
    { type: 'Income', name: 'Shipping Revenue' },
    { type: 'Income', name: 'Layaway Income' },
    { type: 'Income', name: 'Billable Expanse Income' },
    { type: 'Income', name: 'Cash' },
    // Expense / COGS accounts
    { type: 'Expense', name: 'Cost of Goods Sold' },
    { type: 'Expense', name: 'Merchant Services Fees' },
    { type: 'Expense', name: 'Shipping & Freight' },
    { type: 'Expense', name: 'FFL & Licensing Fees' },
    { type: 'Expense', name: 'Rent' },
    { type: 'Expense', name: 'Utilities' },
    { type: 'Expense', name: 'Payroll Expense' },
    { type: 'Expense', name: 'Office Supplies' },
    { type: 'Expense', name: 'Advertising & Marketing' },
  ];
  category_list: {}[] = [];
  expense_records: Expense[] = [];
  sales_records: SalesReceipt[] = [];
  products_services: ProductServiceItem[] = [];
  /** Keyed by item _id — sum of qty_remaining across all open lots. Populated alongside products_services. */
  qty_sums: { [item_id: string]: number } = {};
  vendors: Vendor[] = [];
  vendor_list: { id: string; display_name: string }[] = [];
  customers: Customer[] = [];
  sales_tax_options: salesTax[] = [
    Object.assign(new salesTax(), { name: 'Exempt', state_rate: 0 }),
    Object.assign(new salesTax(), {
      name: 'Idaho Sales Tax',
      state_rate: 0.06,
    }),
  ];

  inventory_alerts: InventoryAlert[] = [
    {
      company_id: '6779614ed587365307a23755',
      alert_selected_vendors: ['RSR Group', `Lipsey's`],
      alert_desired_qty: 10,
      alert_item_upc: '123456789012',
      alert_item_name: 'Product 1',
      alert_item_description: 'Product 1 description',
      alert_text_number: '1234567890',
      alert_email: 'alert1@example.com',
      created_date: '01-01-2023',
      requestor: 'John Doe',
    },
    {
      company_id: '6779614ed587365307a23755',
      alert_selected_vendors: [`Davidson's`],
      alert_desired_qty: 20,
      alert_item_upc: '987654321098',
      alert_item_name: 'Product 2',
      alert_item_description: 'Product 2 description',
      alert_text_number: '0987654321',
      alert_email: 'alert2@example.com',
      created_date: '02-01-2023',
      requestor: 'Jane Smith',
    },
    {
      company_id: '6779614ed587365307a23755',
      alert_selected_vendors: ['Chattanooga Shooting Supply', `Lipsey's`],
      alert_desired_qty: 15,
      alert_item_upc: '112233445566',
      alert_item_name: 'Product 3',
      alert_item_description: 'Product 3 description',
      alert_text_number: '5555555555',
      alert_email: 'alert3@example.com',
      created_date: '03-01-2023',
      requestor: 'Alice Johnson',
    },
  ];
  auto_purchase: AutoPurchase[] = [
    {
      company_id: '6779614ed587365307a23755',
      auto_purchase_selected_vendors: [`RSR Group`, `Lipsey's`],
      auto_purchase_desired_qty: 10,
      auto_purchase_item_upc: '123456789012',
      auto_purchase_item_name: 'Product 1',
      auto_purchase_item_description: 'Product 1 description',
      auto_purchase_starting_date: '01-01-2023',
      auto_purchase_ending_date: '01-31-2023',
      auto_purchase_partial_orders_allowed: true,
      created_date: '01-01-2023',
      requestor: 'John Doe',
    },
    {
      company_id: '6779614ed587365307a23755',
      auto_purchase_selected_vendors: [`Davidson's`],
      auto_purchase_desired_qty: 20,
      auto_purchase_item_upc: '987654321098',
      auto_purchase_item_name: 'Product 2',
      auto_purchase_item_description: 'Product 2 description',
      auto_purchase_starting_date: '02-01-2023',
      auto_purchase_ending_date: '02-28-2023',
      auto_purchase_partial_orders_allowed: false,
      created_date: '02-01-2023',
      requestor: 'Jane Smith',
    },
    {
      company_id: '6779614ed587365307a23755',
      auto_purchase_selected_vendors: [
        `Chattanooga Shooting Supply`,
        `Lipsey's`,
      ],
      auto_purchase_desired_qty: 15,
      auto_purchase_item_upc: '112233445566',
      auto_purchase_item_name: 'Product 3',
      auto_purchase_item_description: 'Product 3 description',
      auto_purchase_starting_date: '03-01-2023',
      auto_purchase_ending_date: '03-31-2023',
      auto_purchase_partial_orders_allowed: true,
      created_date: '03-01-2023',
      requestor: 'Alice Johnson',
    },
  ];
  auto_purchase_history: PurchaseHistory[] = [
    {
      company_id: '6779614ed587365307a23755',
      purchase_date: '01-01-2023 12:34:00',
      vendor_name: `RSR Group`,
      item_upc: '123456789012',
      item_name: 'Auto Part A',
      item_description: 'Description for Auto Part A',
      desired_qty: 10,
      purchase_qty: 10,
      item_price: 15.99,
      purchase_total: 159.9,
      order_number: 'ORD001',
    },
    {
      company_id: '6779614ed587365307a23755',
      purchase_date: '02-15-2023 01:26:00',
      vendor_name: `Lipsey's`,
      item_upc: '234567890123',
      item_name: 'Auto Part B',
      item_description: 'Description for Auto Part B',
      desired_qty: 5,
      purchase_qty: 5,
      item_price: 25.5,
      purchase_total: 127.5,
      order_number: 'ORD002',
    },
    {
      company_id: '6779614ed587365307a23755',
      purchase_date: '03-10-2023 03:14:00',
      vendor_name: `RSR Group`,
      item_upc: '345678901234',
      item_name: 'Auto Part C',
      item_description: 'Description for Auto Part C',
      desired_qty: 20,
      purchase_qty: 20,
      item_price: 9.99,
      purchase_total: 199.8,
      order_number: 'ORD003',
    },
    {
      company_id: '6779614ed587365307a23755',
      purchase_date: '04-05-2023 16:44:00',
      vendor_name: `Davidson's`,
      item_upc: '456789012345',
      item_name: 'Auto Part D',
      item_description: 'Description for Auto Part D',
      desired_qty: 7,
      purchase_qty: 7,
      item_price: 30.0,
      purchase_total: 210.0,
      order_number: 'ORD004',
    },
    {
      company_id: '6779614ed587365307a23755',
      purchase_date: '05-20-2023 11:33:00',
      vendor_name: `Lipsey's`,
      item_upc: '567890123456',
      item_name: 'Auto Part E',
      item_description: 'Description for Auto Part E',
      desired_qty: 12,
      purchase_qty: 12,
      item_price: 12.5,
      purchase_total: 150.0,
      order_number: 'ORD005',
    },
  ];
  filtered_row_count: number = 0;
  constructor(
    private router: Router,
    private http: HttpClient,
    private sharedService: SharedService,
    private localDb: LocalDatabaseService,
    private networkSync: NetworkSyncService,
    private websocket: WebsocketService,
  ) {
    // Merge custom accounts whenever company info is (re)loaded
    this.sharedService.company_info_retrieved$.subscribe(() => {
      this.mergeCustomAccounts();
    });

    // Live updates from MongoDB change streams (other users editing the same data).
    this.websocket.collectionChanges$
      .pipe(filter((e) => e.collection === 'customers'))
      .subscribe((e) => this.applyCustomerChange(e));

    // Sales receipts: keep other logged-in sessions' sales table in sync
    // when someone adds/edits/voids/deletes a receipt.
    this.websocket.collectionChanges$
      .pipe(filter((e) => e.collection === 'sales_receipts'))
      .subscribe((e) => this.applySalesReceiptChange(e));

    // Inventory lots: refresh the qty-sums map on any change (insert, update,
    // delete). Debounced because a single expense save can create multiple
    // lots in quick succession — one refetch is enough for the burst.
    this.websocket.collectionChanges$
      .pipe(filter((e) => e.collection === 'inventory_lots'))
      .subscribe(() => this.scheduleQtySumsRefresh());

    // Products/Services: keep other logged-in sessions' grid in sync when
    // someone adds/edits/deletes an item.
    this.websocket.collectionChanges$
      .pipe(filter((e) => e.collection === 'product_service_items'))
      .subscribe((e) => this.applyProductServiceChange(e));
  }

  /** Debounce timer for qty-sums refresh. Coalesces rapid lot events
   * (e.g. multi-line expense save) into a single refetch. */
  private qtySumsRefreshTimer: any = null;
  /** Subject that fires after qty_sums is refreshed, so components binding
   * to it can re-render even with OnPush change detection. */
  qty_sums_updated$: Subject<{ [item_id: string]: number }> = new Subject();

  private scheduleQtySumsRefresh(): void {
    if (this.qtySumsRefreshTimer) clearTimeout(this.qtySumsRefreshTimer);
    this.qtySumsRefreshTimer = setTimeout(() => {
      this.qtySumsRefreshTimer = null;
      this.refreshQtySums();
    }, 250);
  }

  /** Re-fetch the qty-summary endpoint and update the in-memory map. */
  async refreshQtySums(): Promise<void> {
    try {
      const sumRes = await firstValueFrom(
        this.http.get<{ qty_sums: { [id: string]: number } }>(
          environment.apiUrl + '/inventory_lots/qty-summary',
        ),
      );
      this.qty_sums = sumRes.qty_sums || {};
      this.qty_sums_updated$.next(this.qty_sums);
    } catch {
      // Non-fatal: leave qty_sums as-is (empty or stale).
    }
  }

  /** Apply a live change-stream event to the in-memory customer cache.
   * The local writer already updated `this.customers` optimistically; events
   * for our own writes will re-converge to the server state, which is fine. */
  private applyCustomerChange(event: CollectionChangeEvent): void {
    const id = event.id;
    if (!id) return;

    if (event.op === 'delete' || event.doc?.deleted) {
      this.customers = this.customers.filter((c) => c.id !== id);
      this.customer_list_updated$.next([...this.customers]);
      this.localDb.customers.delete(id).catch(() => {});
      return;
    }

    if (!event.doc) return;

    const idx = this.customers.findIndex((c) => c.id === id);
    if (idx === -1) {
      this.customers = [...this.customers, event.doc];
    } else {
      this.customers = this.customers.map((c, i) =>
        i === idx ? event.doc : c,
      );
    }
    this.customer_list_updated$.next([...this.customers]);
    this.localDb.customers.put(event.doc).catch(() => {});
  }

  /** Apply a live change-stream event to the in-memory sales receipt cache.
   * Reuses `sales_records_list_retrieved$` since that's the signal the sales
   * table already listens to — other sessions pick up adds/edits/deletes
   * without needing to manually refresh. The local writer already updated
   * `this.sales_records` optimistically; events for our own writes will
   * re-converge to the server state, which is fine. */
  private applySalesReceiptChange(event: CollectionChangeEvent): void {
    const id = event.id;
    if (!id) return;

    if (event.op === 'delete') {
      this.sales_records = this.sales_records.filter((r) => r.id !== id);
      this.sales_records_list_retrieved$.next(this.sales_records);
      this.localDb.salesReceipts.delete(id).catch(() => {});
      return;
    }

    if (!event.doc) return;

    const idx = this.sales_records.findIndex((r) => r.id === id);
    if (idx === -1) {
      this.sales_records = [...this.sales_records, event.doc as SalesReceipt];
    } else {
      this.sales_records = this.sales_records.map((r, i) =>
        i === idx ? (event.doc as SalesReceipt) : r,
      );
    }
    this.sales_records_list_retrieved$.next(this.sales_records);
    this.localDb.salesReceipts.put(event.doc).catch(() => {});
  }

  /** Apply a live change-stream event to the in-memory products/services cache.
   * The local writer already updated `this.products_services` optimistically;
   * events for our own writes will re-converge to the server state, which is fine. */
  private applyProductServiceChange(event: CollectionChangeEvent): void {
    const id = event.id;
    if (!id) return;

    if (event.op === 'delete' || event.doc?.deleted) {
      this.products_services = this.products_services.filter((p) => p.id !== id);
      this.products_services_list_updated$.next([...this.products_services]);
      this.localDb.productsServices.delete(id).catch(() => {});
      return;
    }

    if (!event.doc) return;

    const idx = this.products_services.findIndex((p) => p.id === id);
    if (idx === -1) {
      this.products_services = [...this.products_services, event.doc as ProductServiceItem];
    } else {
      this.products_services = this.products_services.map((p, i) =>
        i === idx ? (event.doc as ProductServiceItem) : p,
      );
    }
    this.products_services_list_updated$.next([...this.products_services]);
    this.localDb.productsServices.put(event.doc).catch(() => {});
  }

  /** Append any custom_accounts from company settings that aren't already in the defaults.
   * Also syncs sales_tax_options from company accounting_options. */
  mergeCustomAccounts() {
    const custom: { type: string; name: string }[] =
      this.sharedService.company_info?.accounting_options?.custom_accounts ||
      [];
    for (const acct of custom) {
      const exists = this.income_expense_accounts.some(
        (a) =>
          a.type === acct.type &&
          a.name.toLowerCase() === acct.name.toLowerCase(),
      );
      if (!exists) {
        this.income_expense_accounts = [
          ...this.income_expense_accounts,
          acct as Account,
        ];
      }
    }
    const storedTax =
      this.sharedService.company_info?.accounting_options?.sales_tax_options;
    if (storedTax?.length) {
      this.sales_tax_options = storedTax.map((t: any) =>
        Object.assign(new salesTax(), t),
      );
    }
    // Merge built-in defaults with the company's saved customs.
    // Defaults always render first; customs that happen to match a default
    // (case-insensitive) are dropped so we don't show duplicates.
    const customPaymentAccounts =
      this.sharedService.company_info?.accounting_options?.payment_accounts ??
      [];
    this.payment_accounts = [
      ...this.payment_account_defaults,
      ...customPaymentAccounts.filter(
        (c: string) =>
          !this.payment_account_defaults.some(
            (d) => d.toLowerCase() === c.toLowerCase(),
          ),
      ),
    ];
    const customPaymentMethods =
      this.sharedService.company_info?.accounting_options?.payment_methods ??
      [];
    this.payment_methods = [
      ...this.payment_method_defaults,
      ...customPaymentMethods.filter(
        (c: string) =>
          !this.payment_method_defaults.some(
            (d) => d.toLowerCase() === c.toLowerCase(),
          ),
      ),
    ];
  }
  contact_created: Subject<Contact> = new Subject();

  //-----------Vendor CRUD Functions MongoDB-----------------
  async addNewVendor(vendor_entry: Vendor) {
    vendor_entry.company_id = this.sharedService.company_info.company_id;
    this.sharedService.openStatusSpinner('Adding Vendor');
    try {
      if (!vendor_entry.id) {
        vendor_entry.id = uuidv4();
      }

      const transformedVendor = transformVendorToPlainObject(vendor_entry);
      await this.localDb.vendors.put(transformedVendor);
      await this.networkSync.queueOperation(
        'POST',
        environment.apiUrl + '/vendors',
        vendor_entry,
      );

      this.vendors.push(transformedVendor);
      this.vendors = sortByDisplayName(this.vendors);
      this.vendors = [...this.vendors];
      this.vendor_list_updated$.next(this.vendors);
      this.sharedService.closeStatusSpinner();
    } catch (error) {
      console.error('Error:', error);
      this.sharedService.closeStatusSpinner();
      this.sharedService.raiseErrorToast(error);
    }
  }
  async addMultipleVendors(vendor_entries: Vendor[]) {
    vendor_entries = vendor_entries.map((vendor) => {
      vendor.company_id = this.sharedService.company_info.company_id;
      return vendor;
    });

    this.sharedService.openStatusSpinner('Importing Vendors');
    this.http
      .post<{
        message: string;
        vendors: string[];
      }>(environment.apiUrl + '/vendors/bulk', vendor_entries)
      .subscribe({
        next: (response) => {
          vendor_entries.forEach((vendor, index) => {
            vendor.id = response.vendors[index];
            this.vendors.push(vendor);
          });
          this.vendor_list_updated$.next(this.vendors);
          this.sharedService.closeStatusSpinner();
        },
        error: (error) => {
          console.error('Error:', error);
          this.sharedService.closeStatusSpinner();
          this.sharedService.raiseErrorToast(error);
        },
      });
  }
  async getVendors(preferCache = false) {
    if (preferCache) {
      const localVendors = await this.localDb.vendors
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.vendors = localVendors.sort((a, b) =>
        a.contact.display_name.trim().localeCompare(b.contact.display_name.trim()),
      );
      this.vendor_list_retrieved$.next(this.vendors);
      return;
    }
    let params = new HttpParams().set(
      'company_id',
      this.sharedService.company_info.company_id || '',
    );

    try {
      const response = await firstValueFrom(
        this.http.get<{ message: string; vendor_list: any[] }>(
          environment.apiUrl + '/vendors',
          { params },
        ),
      );

      const transformedResponse = response.vendor_list.map((entry) => {
        return {
          id: entry._id,
          company_id: entry.company_id,
          contact_id: entry.contact_id,
          contact_type: entry.contact_type,
          contact: {
            company_name: entry.contact.company_name,
            display_name: entry.contact.display_name,
            name_title: entry.contact.name_title,
            name_first: entry.contact.name_first,
            name_middle: entry.contact.name_middle,
            name_last: entry.contact.name_last,
            name_suffix: entry.contact.name_suffix,
            email: entry.contact.email,
            phone_number: entry.contact.phone_number,
            fax_number: entry.contact.fax_number,
            website: entry.contact.website,
            name_to_print_on_checks: entry.contact.name_to_print_on_checks,
            other: entry.contact.other,
          },
          address: {
            street: entry.address ? entry.address.street : null,
            street2: entry.address ? entry.address.street2 : null,
            city: entry.address ? entry.address.city : null,
            state: entry.address ? entry.address.state : null,
            zip_code: entry.address ? entry.address.zip_code : null,
            country: entry.address ? entry.address.country : null,
          },
          ach_payments: {
            account_number: entry.ach_payments
              ? entry.ach_payments.account_number
              : null,
            routing_number: entry.ach_payments
              ? entry.ach_payments.routing_number
              : null,
          },
          memo: entry.memo,
          attachments: [],
          ffl: {
            ffl: entry.ffl ? entry.ffl.ffl : null,
            legal_name: entry.ffl ? entry.ffl.legal_name : null,
            business_name: entry.ffl ? entry.ffl.business_name : null,
            phone_number: entry.ffl ? entry.ffl.phone_number : null,
            email: entry.ffl ? entry.ffl.email : null,
            notes: entry.ffl ? entry.ffl.notes : null,
            expiration_date: entry.ffl ? entry.ffl.expiration_date : null,
          },
        };
      });

      this.vendors = transformedResponse.sort((a, b) =>
        a.contact.display_name
          .trim()
          .localeCompare(b.contact.display_name.trim()),
      );
      this.vendor_list_retrieved$.next(this.vendors);

      // Cache all vendors locally — clear stale records first so server-deleted items are removed
      await this.localDb.vendors
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .delete();
      await this.localDb.vendors.bulkPut(this.vendors);
    } catch (error) {
      console.warn(
        'Network fetch failed, falling back to IndexedDB for vendors...',
        error,
      );
      const localVendors = await this.localDb.vendors
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();

      this.vendors = localVendors.sort((a, b) =>
        a.contact.display_name
          .trim()
          .localeCompare(b.contact.display_name.trim()),
      );
      this.vendor_list_retrieved$.next(this.vendors);
    }
  }
  async editVendor(vendor_to_edit: Vendor) {
    vendor_to_edit.company_id = this.sharedService.company_info.company_id;
    vendor_to_edit.updatedAt = new Date().toISOString();
    // Optimistic update in memory and IndexedDB
    this.vendors = this.vendors.map((e) =>
      e.id === vendor_to_edit.id ? vendor_to_edit : e,
    );
    this.vendor_list_updated$.next([...this.vendors]);
    await this.localDb.vendors.put(vendor_to_edit);
    // Queue PUT for when online
    await this.networkSync.queueOperation(
      'PUT',
      environment.apiUrl + '/vendors/' + vendor_to_edit.id,
      vendor_to_edit,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      this.sharedService.raiseSuccessToast(
        Statements.vendor_successfully_updated,
      );
    }
  }
  async deleteVendor(vendor_to_delete: Vendor) {
    // Optimistic delete in memory and IndexedDB
    this.vendors = this.vendors.filter((v) => v.id !== vendor_to_delete.id);
    this.vendor_list_updated$.next([...this.vendors]);
    await this.localDb.vendors.delete(vendor_to_delete.id);
    // Queue DELETE for when online
    await this.networkSync.queueOperation(
      'DELETE',
      environment.apiUrl + '/vendors/' + vendor_to_delete.id,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      this.sharedService.raiseSuccessToast(
        Statements.vendor_successfully_deleted,
      );
    }
  }

  //-----------Customer CRUD Functions MongoDB-----------------
  async addNewCustomer(customer_entry: Customer) {
    customer_entry.company_id = this.sharedService.company_info.company_id;
    // Optimistic add to local state
    this.customers = [...this.customers, customer_entry];
    this.customer_list_updated$.next(this.customers);
    this.customer_added$.next(customer_entry);
    // Queue POST for when online
    await this.networkSync.queueOperation(
      'POST',
      environment.apiUrl + '/customers',
      customer_entry,
    );
    if (this.networkSync.isOnline) {
      // queueOperation kicks off the actual HTTP POST in the background; wait
      // for the queue to drain before refetching, otherwise the GET races and
      // returns the list without the new record (overwriting the optimistic add).
      await this.networkSync.waitForSync();
      await this.getCustomers();
    }
  }
  async addMultipleCustomers(customer_entries: Customer[]) {
    customer_entries = customer_entries.map((customer) => {
      customer.company_id = this.sharedService.company_info.company_id;
      return customer;
    });

    this.http
      .post<{
        message: string;
        customers: string[];
      }>(environment.apiUrl + '/customers/bulk', customer_entries)
      .subscribe({
        next: (response) => {
          customer_entries.forEach((customer_entry, index) => {
            customer_entry.id = response.customers[index];
            this.customers.push(customer_entry);
          });
          this.customer_list_updated$.next(this.customers);
        },
        error: (error) => {
          console.error('Error:', error);
          this.sharedService.raiseErrorToast(error);
        },
      });
  }
  async getCustomers(preferCache = false) {
    if (preferCache) {
      const localCustomers = await this.localDb.customers
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.customers = localCustomers.sort((a, b) =>
        a.contact?.display_name?.trim().localeCompare(b.contact?.display_name?.trim()),
      );
      this.customer_list_retrieved$.next(this.customers);
      return;
    }
    try {
      let params = new HttpParams().set(
        'company_id',
        this.sharedService.company_info.company_id || '',
      );
      const response = await firstValueFrom(
        this.http
          .get<{
            message: string;
            customer_list: any[];
          }>(environment.apiUrl + '/customers', { params })
          .pipe(
            map((res) =>
              res.customer_list.map((entry) => {
                const contact = entry.contact || {};
                return {
                  id: entry._id,
                  company_id: entry.company_id,
                  contact_id: entry.contact_id,
                  contact_type: entry.contact_type,
                  contact: {
                    company_name: contact.company_name,
                    display_name: contact.display_name,
                    name_title: contact.name_title,
                    name_first: contact.name_first,
                    name_middle: contact.name_middle,
                    name_last: contact.name_last,
                    name_suffix: contact.name_suffix,
                    email: contact.email,
                    phone_number: contact.phone_number,
                    fax_number: contact.fax_number,
                    website: contact.website,
                    name_to_print_on_checks: contact.name_to_print_on_checks,
                    other: contact.other,
                  },
                  address: {
                    street: entry.address ? entry.address.street : null,
                    street2: entry.address ? entry.address.street2 : null,
                    city: entry.address ? entry.address.city : null,
                    state: entry.address ? entry.address.state : null,
                    zip_code: entry.address ? entry.address.zip_code : null,
                    country: entry.address ? entry.address.country : null,
                  },
                  tax_option: Object.assign(new salesTax(), {
                    name: entry.tax_option ? entry.tax_option.name : null,
                    state_rate: entry.tax_option
                      ? (entry.tax_option.state_rate ??
                        entry.tax_option.tax_rate ??
                        0)
                      : 0,
                    county_rate: entry.tax_option
                      ? (entry.tax_option.county_rate ?? 0)
                      : 0,
                    city_rate: entry.tax_option
                      ? (entry.tax_option.city_rate ?? 0)
                      : 0,
                    special_rate: entry.tax_option
                      ? (entry.tax_option.special_rate ?? 0)
                      : 0,
                  }),
                  memo: entry.memo,
                  attachments: [],
                };
              }),
            ),
          ),
      );
      const repairedCustomers = this.repairInvalidCustomerContactIds(response);
      this.customers = repairedCustomers.sort((a, b) =>
        a.contact.display_name
          ?.trim()
          .localeCompare(b.contact.display_name?.trim()),
      );
      this.customer_list_retrieved$.next(this.customers);
      // Cache locally for offline use — clear stale records first so server-deleted items are removed
      await this.localDb.customers
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .delete();
      await this.localDb.customers.bulkPut(this.customers);
    } catch (error) {
      console.warn(
        'Network fetch failed, falling back to IndexedDB for customers...',
        error,
      );
      const localCustomers = await this.localDb.customers
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.customers = localCustomers.sort((a, b) =>
        a.contact?.display_name
          ?.trim()
          .localeCompare(b.contact?.display_name?.trim()),
      );
      this.customer_list_retrieved$.next(this.customers);
      if (localCustomers.length === 0) {
        this.sharedService.raiseErrorToast(
          'You are offline and no customer data is cached yet.',
        );
      }
    }
  }
  async editCustomer(customer_to_edit: Customer) {
    customer_to_edit.company_id = this.sharedService.company_info.company_id;
    customer_to_edit.updatedAt = new Date().toISOString();
    // Optimistic update in memory and IndexedDB
    this.customers = this.customers.map((e) =>
      e.id === customer_to_edit.id ? customer_to_edit : e,
    );
    this.customer_list_updated$.next([...this.customers]);
    await this.localDb.customers.put(customer_to_edit);
    // Queue PUT for when online
    await this.networkSync.queueOperation(
      'PUT',
      environment.apiUrl + '/customers/' + customer_to_edit.id,
      customer_to_edit,
    );
    if (this.networkSync.isOnline) {
      this.sharedService.raiseSuccessToast(
        Statements.customer_successfully_updated,
      );
    }
    this.sharedService.closeStatusSpinner();
  }
  async deleteCustomer(customer_to_delete: Customer) {
    // Optimistic delete in memory and IndexedDB
    this.customers = this.customers.filter(
      (c) => c.id !== customer_to_delete.id,
    );
    this.customer_list_updated$.next([...this.customers]);
    await this.localDb.customers.delete(customer_to_delete.id);
    // Queue DELETE for when online
    await this.networkSync.queueOperation(
      'DELETE',
      environment.apiUrl + '/customers/' + customer_to_delete.id,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      this.sharedService.raiseSuccessToast(
        Statements.customer_successfully_deleted,
      );
    }
  }

  //-----------Sales Receipt CRUD Functions MongoDB-----------------
  /** Fetch the highest numeric portion of any sales receipt number for the
   * authenticated company. Use this to compute the next "SRNNN" number
   * server-side, which avoids the multi-user race where two browsers compute
   * the same next number from stale local caches. Falls back to the locally
   * cached list when offline or if the request fails. */
  async getMaxSalesReceiptNumber(): Promise<number> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ max_number: number }>(
          environment.apiUrl + '/sales_receipts/max-number',
        ),
      );
      return response?.max_number ?? 0;
    } catch (error) {
      console.warn(
        'Falling back to local list for max sales receipt number',
        error,
      );
      const numbers = this.sales_records
        .map((r) => +((r.sales_receipt_number ?? '').substring(2) || 0))
        .filter((n) => !isNaN(n));
      return numbers.length > 0 ? Math.max(...numbers) : 0;
    }
  }

  async addNewSalesReceipt(sales_receipt_entry: SalesReceipt): Promise<void> {
    sales_receipt_entry.company_id = this.sharedService.company_info.company_id;
    // Optimistic add
    this.sales_records = [...this.sales_records, sales_receipt_entry];
    this.sales_records_list_updated$.next(this.sales_records);
    // Queue POST for when online
    await this.networkSync.queueOperation(
      'POST',
      environment.apiUrl + '/sales_receipts',
      sales_receipt_entry,
    );
    if (this.networkSync.isOnline) {
      // queueOperation kicks off the actual HTTP POST in the background; wait
      // for the queue to drain before refetching, otherwise the GET races and
      // returns the list without the new record (overwriting the optimistic add).
      await this.networkSync.waitForSync();
      await this.getSalesReceipts();
      this.sharedService.raiseSuccessToast(
        Statements.sales_receipt_successfully_added,
      );
    }
    this.sharedService.closeStatusSpinner();
  }
  async getSalesReceipts(preferCache = false) {
    if (preferCache) {
      const localRecords = await this.localDb.salesReceipts
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.sales_records = localRecords as SalesReceipt[];
      this.sales_records_list_retrieved$.next(this.sales_records);
      return;
    }
    try {
      let params = new HttpParams().set(
        'company_id',
        this.sharedService.company_info.company_id || '',
      );
      const response = await firstValueFrom(
        this.http
          .get<{
            message: string;
            sales_receipt_list: any[];
          }>(environment.apiUrl + '/sales_receipts', { params })
          .pipe(
            map((res) =>
              res.sales_receipt_list.map((entry) => ({
                id: entry._id,
                company_id: entry.company_id,
                sales_receipt_number: entry.sales_receipt_number,
                type: entry.type,
                customer_id: entry.customer_id,
                customer_name: entry.customer_name,
                email: entry.email,
                billing_address: entry.billing_address,
                receipt_date: entry.receipt_date,
                sales_rep: entry.sales_rep,
                payment_method: entry.payment_method,
                reference_number: entry.reference_number,
                sales_items: entry.sales_items,
                message_on_receipt: entry.message_on_receipt,
                message_on_statement: entry.message_on_statement,
                subtotal: entry.subtotal,
                taxable_subtotal: entry.taxable_subtotal,
                sales_tax_rate: entry.sales_tax_rate,
                total: entry.total,
                amount_received: entry.amount_received,
                balance_due: entry.balance_due,
                status: entry.status,
              })),
            ),
          ),
      );
      this.sales_records = response as SalesReceipt[];
      this.sales_records_list_retrieved$.next(this.sales_records);
      // Cache locally for offline use — clear stale records first so server-deleted items are removed
      await this.localDb.salesReceipts
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .delete();
      await this.localDb.salesReceipts.bulkPut(this.sales_records);
    } catch (error) {
      console.warn(
        'Network fetch failed, falling back to IndexedDB for sales receipts...',
        error,
      );
      const localRecords = await this.localDb.salesReceipts
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.sales_records = localRecords as SalesReceipt[];
      this.sales_records_list_retrieved$.next(this.sales_records);
      if (localRecords.length === 0) {
        this.sharedService.raiseErrorToast(
          'You are offline and no sales receipt data is cached yet.',
        );
      }
    }
  }
  async deleteSalesReceipt(sales_receipt_id: string): Promise<void> {
    // Optimistic delete in memory and IndexedDB
    this.sales_records = this.sales_records.filter(
      (r) => r.id !== sales_receipt_id,
    );
    this.sales_receipt_deleted$.next([...this.sales_records]);
    await this.localDb.salesReceipts.delete(sales_receipt_id);
    // Queue DELETE for when online
    await this.networkSync.queueOperation(
      'DELETE',
      environment.apiUrl + '/sales_receipts/' + sales_receipt_id,
    );
    this.sharedService.closeStatusSpinner();
    if (!this.networkSync.isOnline) {
      this.sharedService.raiseInfoToast(
        'You are offline. Deletion will sync when reconnected.',
      );
    } else {
      this.sharedService.raiseSuccessToast(
        Statements.sales_receipt_successfully_deleted,
      );
      this.sharedService.changeRoute('accounting-sales-table');
    }
  }
  async editSalesReceipt(sales_receipt_to_edit: SalesReceipt) {
    if (!sales_receipt_to_edit?.id) {
      // Fail fast — without an id, we can't PUT to the server or write to
      // IndexedDB (the salesReceipts store uses `id` as its keyPath).
      // Earlier, this manifested as a confusing Dexie DataError.
      console.error(
        'editSalesReceipt called without a sales_receipt id',
        sales_receipt_to_edit,
      );
      this.sharedService.raiseErrorToast(
        'Cannot save: this sales receipt is missing an id. Try reloading.',
      );
      return;
    }
    this.sharedService.openStatusSpinner('Editing Sales Receipt');
    sales_receipt_to_edit.company_id =
      this.sharedService.company_info.company_id;
    sales_receipt_to_edit.updatedAt = new Date().toISOString();
    // Optimistic update in memory and IndexedDB
    this.sales_records = this.sales_records.map((e) =>
      e.id === sales_receipt_to_edit.id ? sales_receipt_to_edit : e,
    );
    this.sales_receipt_updated$.next([...this.sales_records]);
    await this.localDb.salesReceipts.put(sales_receipt_to_edit);
    // Queue PUT for when online
    await this.networkSync.queueOperation(
      'PUT',
      environment.apiUrl + '/sales_receipts/' + sales_receipt_to_edit.id,
      sales_receipt_to_edit,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      this.sharedService.raiseSuccessToast(
        Statements.sales_receipt_successfully_updated,
      );
    }
  }

  //-----------Expense CRUD Functions MongoDB-----------------
  async addNewExpenseRecord(expense_entry: Expense): Promise<void> {
    expense_entry.company_id = this.sharedService.company_info.company_id;
    // Optimistic add
    this.expense_records = [...this.expense_records, expense_entry];
    this.expense_records_list_updated$.next(this.expense_records);
    // Queue POST for when online
    await this.networkSync.queueOperation(
      'POST',
      environment.apiUrl + '/expense_records',
      expense_entry,
    );
    if (this.networkSync.isOnline) {
      // queueOperation kicks off the actual HTTP POST in the background; wait
      // for the queue to drain before refetching, otherwise the GET races and
      // returns the list without the new record (overwriting the optimistic add).
      await this.networkSync.waitForSync();
      await this.getExpenseRecords();
    }
    this.sharedService.closeStatusSpinner();
  }
  async editExpenseRecord(
    expense_record_to_edit: Expense,
    status?: boolean,
  ): Promise<void> {
    if (status === true || status === undefined) {
      this.sharedService.openStatusSpinner('Editing Expense Record');
    }
    expense_record_to_edit.company_id =
      this.sharedService.company_info.company_id;
    expense_record_to_edit.updatedAt = new Date().toISOString();
    // Optimistic update in memory and IndexedDB
    this.expense_records = this.expense_records.map((e) =>
      e.id === expense_record_to_edit.id ? expense_record_to_edit : e,
    );
    this.expense_records = [...this.expense_records];
    this.expense_records_list_updated$.next([...this.expense_records]);
    await this.localDb.expenseRecords.put(expense_record_to_edit);
    // Queue PUT for when online
    await this.networkSync.queueOperation(
      'PUT',
      environment.apiUrl + '/expense_records/' + expense_record_to_edit.id,
      expense_record_to_edit,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      if (expense_record_to_edit.status === FormStatus.deleted) {
        this.sharedService.raiseErrorToast(
          Statements.expense_record_successfully_deleted,
        );
        this.sharedService.changeRoute('accounting-expense-table');
      } else {
        this.sharedService.raiseSuccessToast(
          Statements.expense_record_successfully_updated,
        );
      }
    }
  }
  async getExpenseRecords(preferCache = false) {
    if (preferCache) {
      const localRecords = await this.localDb.expenseRecords
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.expense_records = [...localRecords] as Expense[];
      this.expense_records_list_retrieved$.next(this.expense_records);
      return;
    }
    try {
      let params = new HttpParams().set(
        'company_id',
        this.sharedService.company_info.company_id || '',
      );
      const response = await firstValueFrom(
        this.http
          .get<{
            message: string;
            expense_records_list: any[];
          }>(environment.apiUrl + '/expense_records', { params })
          .pipe(
            map((res) =>
              res.expense_records_list.map((entry) => ({
                id: entry._id,
                company_id: entry.company_id,
                type: entry.type,
                payee: entry.payee,
                payee_id: entry.payee_id,
                payment_account: entry.payment_account,
                payment_date: entry.payment_date,
                payment_method: entry.payment_method,
                reference_number: entry.reference_number,
                category_details: entry.category_details,
                item_details: entry.item_details,
                category: entry.category,
                memo: entry.memo,
                due_date: entry.due_date,
                balance: entry.balance,
                total: entry.total,
                attachments: entry.attachments,
                status: entry.status,
              })),
            ),
          ),
      );
      this.expense_records = response as Expense[];
      this.expense_records = [...this.expense_records];
      this.expense_records_list_retrieved$.next(this.expense_records);
      // Cache locally for offline use — clear stale records first so server-deleted items are removed
      await this.localDb.expenseRecords
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .delete();
      await this.localDb.expenseRecords.bulkPut(this.expense_records);
    } catch (error) {
      console.warn(
        'Network fetch failed, falling back to IndexedDB for expense records...',
        error,
      );
      const localRecords = await this.localDb.expenseRecords
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.expense_records = localRecords as Expense[];
      this.expense_records = [...this.expense_records];
      this.expense_records_list_retrieved$.next(this.expense_records);
      if (localRecords.length === 0) {
        this.sharedService.raiseErrorToast(
          'You are offline and no expense record data is cached yet.',
        );
      }
    }
  }

  //-----------Product/Services CRUD Functions MongoDB-----------------
  async addNewProductService(product_service_entry: ProductServiceItem) {
    product_service_entry.company_id =
      this.sharedService.company_info.company_id;
    product_service_entry.archived = false;
    if (
      product_service_entry.type === InventoryTypes.inventory ||
      product_service_entry.type === 'Product' ||
      product_service_entry.type === 'PRODUCT'
    ) {
      this.sharedService.openStatusSpinner('Adding Product Item');
    } else {
      this.sharedService.openStatusSpinner('Adding Service');
    }
    // Optimistic add to local state and IndexedDB
    this.products_services = [...this.products_services, product_service_entry];
    this.products_services_list_updated$.next(this.products_services);
    if (product_service_entry.id) {
      await this.localDb.productsServices.put(product_service_entry);
    }
    // Queue POST for when online
    await this.networkSync.queueOperation(
      'POST',
      environment.apiUrl + '/product_service_items',
      product_service_entry,
    );
    if (this.networkSync.isOnline) {
      // queueOperation kicks off the actual HTTP POST in the background; wait
      // for the queue to drain before refetching, otherwise the GET races and
      // returns the list without the new record (overwriting the optimistic add).
      await this.networkSync.waitForSync();
      await this.getProductsServices();
    }
    this.sharedService.closeStatusSpinner();
  }
  addMultipleProductService(product_service_entries: ProductServiceItem[]) {
    this.sharedService.openStatusSpinner('Importing Products/Services');
    product_service_entries.forEach(
      (entry) =>
        (entry.company_id = this.sharedService.company_info.company_id),
    );
    this.http
      .post<{
        message: string;
        products_services: string;
      }>(
        environment.apiUrl + '/product_service_items/bulk',
        product_service_entries,
      )
      .subscribe({
        next: (response) => {
          product_service_entries.forEach((product_service_entry, index) => {
            product_service_entry.id = response.products_services[index];
            this.products_services.push(product_service_entry);
          });
          this.products_services_list_updated$.next(this.products_services);
          this.sharedService.closeStatusSpinner();
          this.sharedService.raiseSuccessToast(
            Statements.inventory_successfully_updated,
          );
        },
        error: (error) => {
          console.error('Error:', error);
          this.sharedService.raiseErrorToast(error);
          this.sharedService.closeStatusSpinner();
        },
      });
  }
  async editProductService(
    product_service_to_edit: ProductServiceItem,
    status?: boolean,
  ): Promise<void> {
    if (status === true || status === undefined) {
      this.sharedService.openStatusSpinner('Editing Product/Service');
    }
    product_service_to_edit.company_id =
      this.sharedService.company_info.company_id;
    product_service_to_edit.updatedAt = new Date().toISOString();
    // Optimistic update in memory and IndexedDB
    this.products_services = this.products_services.map((e) =>
      e.id === product_service_to_edit.id ? product_service_to_edit : e,
    );
    this.products_services = [...this.products_services];
    this.products_services_list_updated$.next([...this.products_services]);
    await this.localDb.productsServices.put(product_service_to_edit);
    // Queue PUT for when online
    await this.networkSync.queueOperation(
      'PUT',
      environment.apiUrl + '/product_service_items/' +
        product_service_to_edit.id,
      product_service_to_edit,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      if (
        product_service_to_edit.type === InventoryTypes.inventory ||
        product_service_to_edit.type === 'Product' ||
        product_service_to_edit.type === 'PRODUCT'
      ) {
        this.sharedService.raiseSuccessToast(
          Statements.inventory_successfully_updated,
        );
      } else {
        this.sharedService.raiseSuccessToast(
          Statements.service_successfully_updated,
        );
      }
    }
  }
  convertToPlainObject(item: ProductServiceItem): any {
    return {
      id: item.id,
      company_id: item.company_id,
      name: item.name,
      upc: item.upc,
      type: item.type,
      category: item.category,
      description: item.description,
      serial_item: item.serial_item,
      sale_price_rate: item.sale_price_rate,
      cost: item.cost,
      income_asset_account: item.income_asset_account,
      income_account: item.income_account,
      expense_account: item.expense_account,
      inventory_asset_account: item.inventory_asset_account,
      reorder_point: item.reorder_point,
      sales_tax: item.sales_tax,
      preferred_vendor: item.preferred_vendor,
      active: item.active,
      archived: item.archived,
      avg_cost: item.avg_cost ?? null,
      last_cost: item.last_cost ?? null,
    };
  }
  async deleteProductService(product_service_to_delete: ProductServiceItem) {
    // Optimistic delete in memory and IndexedDB
    this.products_services = this.products_services.filter(
      (p) => p.id !== product_service_to_delete.id,
    );
    this.products_services_list_updated$.next([...this.products_services]);
    await this.localDb.productsServices.delete(product_service_to_delete.id);
    // Queue DELETE for when online
    await this.networkSync.queueOperation(
      'DELETE',
      environment.apiUrl + '/product_service_items/' +
        product_service_to_delete.id,
    );
    this.sharedService.closeStatusSpinner();
    if (this.networkSync.isOnline) {
      this.sharedService.raiseSuccessToast(
        Statements.product_successfully_deleted,
      );
    }
  }
  async getProductsServices(preferCache = false) {
    if (preferCache) {
      const localItems = await this.localDb.productsServices
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.products_services = localItems as ProductServiceItem[];
      this.product_service_retrieved$.next(this.products_services);
      await this.refreshQtySums();
      return;
    }
    try {
      let params = new HttpParams().set(
        'company_id',
        this.sharedService.company_info.company_id || '',
      );
      const response = await firstValueFrom(
        this.http
          .get<{
            message: string;
            product_service_list: any[];
          }>(environment.apiUrl + '/product_service_items', { params })
          .pipe(
            map((res) =>
              res.product_service_list.map((entry) => ({
                id: entry._id,
                company_id: entry.company_id,
                name: entry.name,
                upc: entry.upc,
                type: entry.type,
                category: entry.category,
                description: entry.description,
                serial_item: entry.serial_item,
                sale_price_rate: entry.sale_price_rate,
                cost: entry.cost,
                income_asset_account: entry.income_asset_account,
                income_account: entry.income_account,
                expense_account: entry.expense_account,
                inventory_asset_account: entry.inventory_asset_account,
                reorder_point: entry.reorder_point,
                sales_tax: entry.sales_tax,
                preferred_vendor: entry.preferred_vendor,
                active: entry.active,
                archived: entry.archived,
                avg_cost: entry.avg_cost ?? null,
                last_cost: entry.last_cost ?? null,
              })),
            ),
          ),
      );
      this.products_services = response as ProductServiceItem[];
      this.product_service_retrieved$.next(this.products_services);
      // Cache locally for offline use — clear stale records first so server-deleted items are removed
      await this.localDb.productsServices
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .delete();
      await this.localDb.productsServices.bulkPut(this.products_services);
      // Load lot qty sums so checkInventoryLevels can work without item.qty.
      // Shares the same refresh path used by the inventory_lots change-stream
      // subscriber, so a manual reload and a live update go through one method.
      await this.refreshQtySums();
    } catch (error) {
      console.warn(
        'Network fetch failed, falling back to IndexedDB for products/services...',
        error,
      );
      const localItems = await this.localDb.productsServices
        .where('company_id')
        .equals(this.sharedService.company_info.company_id || '')
        .toArray();
      this.products_services = localItems as ProductServiceItem[];
      this.product_service_retrieved$.next(this.products_services);
      if (localItems.length === 0) {
        this.sharedService.raiseErrorToast(
          'You are offline and no products/services data is cached yet.',
        );
      }
    }
  }
  // need to add a markup % input to add to this function
  importProductsServicesBulk(items_to_import: LiveVendorData[]) {
    let items_to_import_array = [];
    for (const item of items_to_import) {
      const product_service_entry: ProductServiceItem = {
        id: null,
        company_id: this.sharedService.company_info.company_id,
        name: item.upc,
        upc: item.upc,
        type: InventoryTypes.inventory,
        category: item.category,
        description: item.desc1,
        serial_item: this.serial_category.includes(item.category)
          ? true
          : false,
        sale_price_rate: this.findProductLowestCost(item) * 1.1,
        cost: this.findProductLowestCost(item),
        income_asset_account: 'Inventory Asset',
        income_account: 'Merchandise Sales',
        expense_account: 'Cost of Goods Sold',
        inventory_asset_account: 'Inventory Asset',
        reorder_point: 0,
        sales_tax: true,
        preferred_vendor: '',
        active: true,
        archived: false,
      };
      if (
        !this.products_services.some((product) => product['upc'] === item.upc)
      ) {
        items_to_import_array.push(product_service_entry);
      }
    }
    if (items_to_import_array.length > 0) {
      this.sharedService.openStatusSpinner(
        `Importing ${items_to_import_array.length} items`,
      );
      this.http
        .post<{
          message: string;
          products_services: string[];
        }>(
          environment.apiUrl + '/product_service_items/bulk',
          items_to_import_array,
        )
        .subscribe({
          next: (response) => {
            items_to_import_array.forEach((product_service_entry, index) => {
              product_service_entry.id = response.products_services[index];
              this.products_services.push(product_service_entry);
            });
            this.products_services_list_updated$.next(this.products_services);
            this.sharedService.closeStatusSpinner();
          },
          error: (error) => {
            console.error('Error:', error);
            this.sharedService.raiseErrorToast(error);
            this.sharedService.closeStatusSpinner();
          },
        });
    } else {
      this.sharedService.raiseErrorToast(
        'All items are already in the Products and Services list',
      );
    }
  }
  findProductLowestCost(item: LiveVendorData): number {
    const prices = [
      parseFloat(item.zanders_price),
      parseFloat(item.rsr_group_price),
      parseFloat(item.chattanooga_price),
      parseFloat(item.lipseys_price),
      parseFloat(item.davidsons_price),
    ];
    return Math.min(...prices);
  }
  getVendorList() {
    const seenIds = new Set<string>();
    const seenNames = new Set<string>();
    const distinct: { id: string; display_name: string }[] = [];

    for (const v of this.vendors) {
      const name = (v.contact?.company_name || '').trim().toLowerCase();
      if (!seenIds.has(v.id) && !seenNames.has(name)) {
        seenIds.add(v.id);
        seenNames.add(name);
        distinct.push({ id: v.id, display_name: v.contact.company_name });
      }
    }
    this.vendor_list = distinct;
  }

  async buildAndOpenExcelFile(column_headers: string[], file_name: string) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');
    worksheet.addRow(column_headers);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file_name}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  createContactId(contact_type?: string): string {
    if (contact_type === ContactTypes.customer) {
      return this.getNextContactId(
        'CU',
        this.customers.map((c) => c.contact_id),
      );
    } else {
      return this.getNextContactId(
        'VE',
        this.vendors.map((v) => v.contact_id),
      );
    }
  }
  createContactIdForImport(contact_type: string, contact_array: any[]): string {
    const prefix = contact_type === ContactTypes.customer ? 'CU' : 'VE';
    const ids = contact_array.map((contact) => contact.contact_id);
    return this.getNextContactId(prefix, ids);
  }

  private repairInvalidCustomerContactIds(customers: Customer[]): Customer[] {
    const prefix = 'CU';
    const numericIdPattern = new RegExp(`^${prefix}\\d+$`);
    let maxContactId = this.getMaxContactNumber(
      prefix,
      customers.map((c) => c.contact_id),
    );

    return customers.map((customer) => {
      const currentId = customer.contact_id || '';
      let nextContactId: string | null = null;
      if (numericIdPattern.test(currentId)) {
        const numericValue = parseInt(currentId.substring(2), 10);
        if (Number.isFinite(numericValue)) {
          nextContactId =
            numericValue < 1000
              ? this.formatContactId(prefix, numericValue)
              : prefix + numericValue.toString();
        }
      }

      if (!nextContactId) {
        maxContactId += 1;
        nextContactId = this.formatContactId(prefix, maxContactId);
      }
      const updatedCustomer = {
        ...customer,
        contact_id: nextContactId,
      };

      if (updatedCustomer.contact_id === customer.contact_id) {
        return customer;
      }

      this.http
        .put(
          environment.apiUrl + '/customers/' + updatedCustomer.id,
          updatedCustomer,
        )
        .subscribe({
          next: () => {
            console.warn(
              '[Customer] Repaired contact_id for',
              updatedCustomer.id,
              '->',
              updatedCustomer.contact_id,
            );
          },
          error: (error) => {
            console.error('[Customer] Failed to repair contact_id', {
              id: updatedCustomer.id,
              error,
            });
          },
        });

      return updatedCustomer;
    });
  }

  private getNextContactId(
    prefix: string,
    contactIds: (string | null)[],
  ): string {
    const maxContactId = this.getMaxContactNumber(prefix, contactIds);
    return this.formatContactId(prefix, maxContactId + 1);
  }

  private getMaxContactNumber(
    prefix: string,
    contactIds: (string | null)[],
  ): number {
    const pattern = new RegExp(`^${prefix}\\d+$`);
    const numbers = contactIds
      .filter((id) => typeof id === 'string' && pattern.test(id))
      .map((id) => parseInt((id as string).substring(2), 10))
      .filter((value) => Number.isFinite(value));

    return numbers.length > 0 ? Math.max(...numbers) : 0;
  }

  private formatContactId(prefix: string, value: number): string {
    return prefix + value.toString().padStart(3, '0');
  }

  storeCurrentSessionFilters(component_name: string, filters?: {}) {
    let component = this.current_session_filtering.find(
      (component) => component.component_name === component_name,
    );
    if (!component) {
      // If the component is not found, initialize it
      component = {
        component_name: component_name,
        filters: {},
        column_config: [],
      };
      this.current_session_filtering.push(component);
    }
    component.filters = filters;
  }

  storeCurrentSessionColumnConfig(
    component_name: string,
    column_config: ColumnState[],
  ) {
    let component = this.current_session_filtering.find(
      (component) => component.component_name === component_name,
    );
    if (!component) {
      // If the component is not found, initialize it
      component = {
        component_name: component_name,
        filters: {},
        column_config: [],
      };
      this.current_session_filtering.push(component);
    }
    component.column_config = column_config;
  }

  saveCurrentTableSettings(component_name: string) {
    this.sharedService.raiseInfoToast(
      `Saving Table Settings for ${component_name}`,
    );
  }
  getCustomerInfo(selected_customer_id: string): Customer {
    let customer_info: Customer = this.customers.filter(
      (customer) => customer.id === selected_customer_id,
    )[0];
    return customer_info;
  }
  getCustomerDisplayName(customerId: string): string {
    if (!customerId) {
      return 'Disposing to FFL';
    }
    if (customerId.toLowerCase() === 'individual') {
      return 'Individual';
    }
    const customer = this.customers.find((c) => c.id === customerId);
    return customer ? customer.contact.display_name : 'Disposing to FFL';
  }
}
